import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FaMicrophone, FaTimes } from 'react-icons/fa';
import { ClipLoader } from 'react-spinners';
import { useAutosizeTextArea } from '../../hooks/useAutosizeTextArea';
import {
  InputAreaWrapper,
  MessageForm,
  TextInput,
  SendButton,
  AudioButton,
  Countdown,
  AudioPreview,
  CancelButton,
  Tooltip,
  ButtonsContainer
} from './styles.js';
import AudioRecorder from '../AudioRecorder';
import axios from 'axios';

function InputArea({ onSendMessage, inputDisabled }) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [isSending, setIsSending] = useState(false);
  const textAreaRef = useRef(null);

  useAutosizeTextArea(textAreaRef, message);

  useEffect(() => {
    let timer;
    if (isCountingDown && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 800);
    } else if (countdown === 0) {
      setIsCountingDown(false);
      setIsRecording(true);
    }
    return () => clearTimeout(timer);
  }, [isCountingDown, countdown]);

  const handleInputChange = (event) => {
    setMessage(event.target.value);
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    setIsSending(true);
    if (recordedAudio) {
      await sendAudioMessage(recordedAudio);
    } else if (message.trim() !== '') {
      await onSendMessage(message);
      setMessage('');
      setIsSending(false);
    }
  };

  const startRecording = () => {
    setCountdown(3);
    setIsCountingDown(true);
  };

  const handleAudioRecorded = (audioBlob) => {
    setRecordedAudio(audioBlob);
    setIsRecording(false);
    setIsCountingDown(false);
  };

  const cancelAudioRecording = () => {
    setRecordedAudio(null);
    setMessage('');
  };

  const sendAudioMessage = async (audioBlob) => {
    const formData = new FormData();
    formData.append('file', audioBlob, 'recording.mp3');
    try {
      const response = await axios.post(
        // 'http://rctWhisperNodeLoadBalancer-1574442330.us-east-1.elb.amazonaws.com',
        `${API_BASE_URL}/api/v1/openai/audio-transcription`,
        audioBlob,
        {
          headers: {
            'Content-Type': 'audio/mpeg',
          },
        }
      );

      const text = response.data.text;
      storeAudioMessage(text, audioBlob);
      // onSendMessage(text, url);
    } catch (error) {
      console.error('Error sending audio message:', error);
    } finally {
      setRecordedAudio(null);
    }
  };

  const storeAudioMessage = async (text, audioBlob) => {
    const token = localStorage.getItem('token');
    const url = `${API_BASE_URL}/api/v1/openai/audio/store?token=${token}`;
    try {
      const response = await axios.post(url, audioBlob, {
        headers: {
          'Content-Type': 'audio/mpeg',
        },
      });
      const audioUrl = response.data.text;
      onSendMessage(text, audioUrl);
      setIsSending(false);
    } catch (error) {
      console.error('Error sending audio message:', error);
    } finally {
      setRecordedAudio(null);
    }
  };

  return (
    <InputAreaWrapper>
      <MessageForm onSubmit={handleSendMessage} className="message-form">
        {!isRecording && !recordedAudio && !isCountingDown && (
          <>
            <TextInput
              ref={textAreaRef}
              value={message}
              onChange={handleInputChange}
              placeholder="Digite sua resposta..."
              disabled={inputDisabled}
            />
            <AudioButton type="button" className="audio-button" onClick={startRecording} disabled={inputDisabled}>
              <FaMicrophone />
            </AudioButton>
            <SendButton type="submit" disabled={inputDisabled || isSending}>
              {isSending ? <ClipLoader color="#fff" size={20} /> : 'Enviar'}
            </SendButton>
          </>
        )}
        {isCountingDown && <Countdown>Gravação inicia em {countdown}...</Countdown>}
        {isRecording && !recordedAudio && (
          <AudioRecorder onAudioRecorded={handleAudioRecorded} isRecording={isRecording} />
        )}
        {recordedAudio && (
          <>
            <AudioPreview controls>
              <source src={URL.createObjectURL(recordedAudio)} type="audio/mpeg" />
              Your browser does not support the audio element.
            </AudioPreview>
            <span style={{ color: "white" }}>Áudio Recebido. Clique em enviar. </span>
            <ButtonsContainer>
              <Tooltip>
                <CancelButton type="button" onClick={cancelAudioRecording}>
                  <FaTimes />
                </CancelButton>
                <span className="tooltiptext">Cancelar Áudio</span>
              </Tooltip>
              <SendButton type="submit" className="audio-received" disabled={inputDisabled || isSending}>
                {isSending ? <ClipLoader color="#fff" size={20} /> : 'Enviar'}
              </SendButton>
            </ButtonsContainer>
          </>
        )}
      </MessageForm>
    </InputAreaWrapper>
  );
}

InputArea.propTypes = {
  onSendMessage: PropTypes.func.isRequired,
  inputDisabled: PropTypes.bool.isRequired,
};

export default InputArea;
